/**
 * Tender Module Enums
 * Type-safe enum definitions matching backend Java enums
 */

/**
 * Type of tender based on procurement method
 */
export enum TenderType {
  OPEN_TENDER = 'OPEN_TENDER',
  RESTRICTED_TENDER = 'RESTRICTED_TENDER',
  COMPETITIVE_NEGOTIATION = 'COMPETITIVE_NEGOTIATION',
  SINGLE_SOURCE = 'SINGLE_SOURCE',
  FRAMEWORK_AGREEMENT = 'FRAMEWORK_AGREEMENT',
  DYNAMIC_PURCHASING_SYSTEM = 'DYNAMIC_PURCHASING_SYSTEM',
  REQUEST_FOR_PROPOSALS = 'REQUEST_FOR_PROPOSALS',
  REQUEST_FOR_QUOTATIONS = 'REQUEST_FOR_QUOTATIONS'
}

/**
 * Current status of a tender
 */
export enum TenderStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  UNDER_EVALUATION = 'UNDER_EVALUATION',
  AWARDED = 'AWARDED',
  CANCELLED = 'CANCELLED',
  ARCHIVED = 'ARCHIVED'
}

/**
 * Current status of a tender application
 * Aligned with backend ApplicationStatus enum
 */
export enum ApplicationStatus {
  DRAFT = 'DRAFT',
  READY_FOR_SUBMISSION = 'READY_FOR_SUBMISSION',
  SUBMITTED = 'SUBMITTED',
  AWAITING_RESULTS = 'AWAITING_RESULTS',
  AWARDED = 'AWARDED',
  REJECTED = 'REJECTED'
}

/**
 * Type of document in tender or application
 * Aligned with backend DocumentType enum
 */
export enum DocumentType {
  // Tender documents
  SPECIFICATIONS = 'SPECIFICATIONS',
  PLANS = 'PLANS',
  REQUIREMENTS = 'REQUIREMENTS',
  ADDITIONAL = 'ADDITIONAL',
  
  // Application documents
  COVER_LETTER = 'COVER_LETTER',
  CV = 'CV',
  PORTFOLIO = 'PORTFOLIO',
  CERTIFICATIONS = 'CERTIFICATIONS',
  FINANCIAL_PROPOSAL = 'FINANCIAL_PROPOSAL',
  TECHNICAL_PROPOSAL = 'TECHNICAL_PROPOSAL',
  REFERENCES = 'REFERENCES',
  
  // Common
  OTHER = 'OTHER'
}

/**
 * Source from which tender was created or imported
 */
export enum TenderSource {
  MANUAL = 'MANUAL',
  IMPORTED = 'IMPORTED',
  SYSTEM_GENERATED = 'SYSTEM_GENERATED',
  API = 'API'
}

/**
 * Human-readable labels for TenderType enum
 */
export const TenderTypeLabels: Record<TenderType, string> = {
  [TenderType.OPEN_TENDER]: 'Open Tender',
  [TenderType.RESTRICTED_TENDER]: 'Restricted Tender',
  [TenderType.COMPETITIVE_NEGOTIATION]: 'Competitive Negotiation',
  [TenderType.SINGLE_SOURCE]: 'Single Source',
  [TenderType.FRAMEWORK_AGREEMENT]: 'Framework Agreement',
  [TenderType.DYNAMIC_PURCHASING_SYSTEM]: 'Dynamic Purchasing System',
  [TenderType.REQUEST_FOR_PROPOSALS]: 'Request for Proposals (RFP)',
  [TenderType.REQUEST_FOR_QUOTATIONS]: 'Request for Quotations (RFQ)'
}

/**
 * Human-readable labels for TenderStatus enum
 */
export const TenderStatusLabels: Record<TenderStatus, string> = {
  [TenderStatus.DRAFT]: 'Draft',
  [TenderStatus.PUBLISHED]: 'Published',
  [TenderStatus.OPEN]: 'Open',
  [TenderStatus.CLOSED]: 'Closed',
  [TenderStatus.UNDER_EVALUATION]: 'Under Evaluation',
  [TenderStatus.AWARDED]: 'Awarded',
  [TenderStatus.CANCELLED]: 'Cancelled',
  [TenderStatus.ARCHIVED]: 'Archived'
}

/**
 * Human-readable labels for ApplicationStatus enum
 */
export const ApplicationStatusLabels: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'Draft',
  [ApplicationStatus.READY_FOR_SUBMISSION]: 'Ready for Submission',
  [ApplicationStatus.SUBMITTED]: 'Submitted',
  [ApplicationStatus.AWAITING_RESULTS]: 'Awaiting Results',
  [ApplicationStatus.AWARDED]: 'Awarded',
  [ApplicationStatus.REJECTED]: 'Rejected'
}

/**
 * Human-readable labels for DocumentType enum
 */
export const DocumentTypeLabels: Record<DocumentType, string> = {
  // Tender documents
  [DocumentType.SPECIFICATIONS]: 'Specifications (Cahier des charges)',
  [DocumentType.PLANS]: 'Plans (Architectural)',
  [DocumentType.REQUIREMENTS]: 'Requirements (Exigences)',
  [DocumentType.ADDITIONAL]: 'Additional Documents',
  
  // Application documents
  [DocumentType.COVER_LETTER]: 'Cover Letter (Lettre de motivation)',
  [DocumentType.CV]: 'CV (Curriculum Vitae)',
  [DocumentType.PORTFOLIO]: 'Portfolio',
  [DocumentType.CERTIFICATIONS]: 'Certifications',
  [DocumentType.FINANCIAL_PROPOSAL]: 'Financial Proposal (Proposition financière)',
  [DocumentType.TECHNICAL_PROPOSAL]: 'Technical Proposal (Proposition technique)',
  [DocumentType.REFERENCES]: 'References (Références)',
  
  // Common
  [DocumentType.OTHER]: 'Other'
}

/**
 * Human-readable labels for TenderSource enum
 */
export const TenderSourceLabels: Record<TenderSource, string> = {
  [TenderSource.MANUAL]: 'Manual Entry',
  [TenderSource.IMPORTED]: 'Imported',
  [TenderSource.SYSTEM_GENERATED]: 'System Generated',
  [TenderSource.API]: 'API'
}

/**
 * Color coding for tender status (for UI badges/chips)
 */
export const TenderStatusColors: Record<TenderStatus, string> = {
  [TenderStatus.DRAFT]: 'gray',
  [TenderStatus.PUBLISHED]: 'blue',
  [TenderStatus.OPEN]: 'green',
  [TenderStatus.CLOSED]: 'orange',
  [TenderStatus.UNDER_EVALUATION]: 'purple',
  [TenderStatus.AWARDED]: 'success',
  [TenderStatus.CANCELLED]: 'error',
  [TenderStatus.ARCHIVED]: 'gray'
}

/**
 * Color coding for application status (for UI badges/chips)
 */
export const ApplicationStatusColors: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'gray',
  [ApplicationStatus.READY_FOR_SUBMISSION]: 'blue',
  [ApplicationStatus.SUBMITTED]: 'info',
  [ApplicationStatus.AWAITING_RESULTS]: 'purple',
  [ApplicationStatus.AWARDED]: 'success',
  [ApplicationStatus.REJECTED]: 'error'
}
